/**
 * Key change for library tracks (2026-09-26): shift a playing track up or down
 * in semitones without changing its tempo, live, in the browser.
 *
 * HOW. The page's one <audio> element (useTrackPlayer) is routed through Web
 * Audio into Signalsmith Stretch (MIT, WASM in an AudioWorklet, loaded on first
 * use from public/vendor, NOT bundled: see loadStretch.js) and out to the speakers. Nothing is stored or
 * re-encoded; the file in Storage is untouched.
 *
 * AT "ORIGINAL" (0) THE STRETCH NODE IS OUT OF THE PATH: source goes straight
 * to the speakers. The node adds a little latency and CPU, and a track nobody
 * transposed should sound exactly as it did before this existed.
 *
 * TWO TRAPS THIS DEPENDS ON, both handled in useTrackPlayer:
 *  - A cross-origin <audio> routed into Web Audio outputs SILENCE, with no
 *    error, unless it was loaded with crossOrigin="anonymous" AND the server
 *    sent Access-Control-Allow-Origin. The Storage bucket needs a CORS rule
 *    for that (storage.cors.json at the repo root). The player sets
 *    crossOrigin and, if the load then fails, reloads without it and turns
 *    key change off rather than going silent.
 *  - createMediaElementSource can be called ONCE per element, ever; after
 *    that the element only sounds through this graph. So the graph is built
 *    lazily on the first key change and then kept for the element's life.
 *
 * Range ±6 semitones: the useful range for fitting a voice, and past about
 * ±4 any time-stretch starts to sound processed. Formant compensation keeps a
 * sung vocal from going chipmunk or giant when shifted.
 */

import loadStretch from './loadStretch';

export const MIN_SEMITONES = -6;
export const MAX_SEMITONES = 6;

export const clampSemitones = (n) => {
	const v = Math.round(Number(n) || 0);
	return Math.max(MIN_SEMITONES, Math.min(MAX_SEMITONES, v));
};

/** "Original", "+2", "−3" (a real minus sign; it reads as one at a glance). */
export const keyLabel = (n) => (!n ? 'Original' : n > 0 ? `+${n}` : `−${Math.abs(n)}`);

const AudioContextClass = () =>
	typeof window !== 'undefined' ? window.AudioContext || window.webkitAudioContext || null : null;

/** Can this browser do it at all? (jsdom, very old Safari: no.) */
export const keyShiftSupported = () => {
	const AC = AudioContextClass();
	return Boolean(AC && typeof AC.prototype.createMediaElementSource === 'function' && 'audioWorklet' in AC.prototype);
};

/**
 * One shifter per <audio> element. `load` is injectable for tests; in the app
 * it is the dynamic import of the library.
 */
export const createKeyShifter = (audioEl, { load = loadStretch } = {}) => {
	let ctx = null;
	let source = null;
	let stretch = null;
	let stretchPromise = null;
	let routedThroughStretch = false;
	let current = 0;
	// Extra sources mixed in BEFORE the stretch (2026-09-28, Rehearsal mode):
	// the kept Vocals stem plays beside the Track, each through its own gain,
	// and both move key together because they share the one stretch node.
	const extras = [];

	const ensureGraph = () => {
		if (ctx) return;
		const AC = AudioContextClass();
		ctx = new AC();
		source = ctx.createMediaElementSource(audioEl);
		source.connect(ctx.destination);
	};

	const ensureStretch = () => {
		if (!stretchPromise) {
			stretchPromise = load().then(async (mod) => {
				const SignalsmithStretch = mod.default || mod;
				stretch = await SignalsmithStretch(ctx);
				stretch.connect(ctx.destination);
				return stretch;
			});
		}
		return stretchPromise;
	};

	const route = (throughStretch) => {
		if (throughStretch === routedThroughStretch) return;
		const target = throughStretch ? stretch : ctx.destination;
		source.disconnect();
		source.connect(target);
		for (const x of extras) {
			x.gain.disconnect();
			x.gain.connect(target);
		}
		routedThroughStretch = throughStretch;
	};

	return {
		get semitones() {
			return current;
		},
		/** Call from a tap: browsers only let an AudioContext start on a gesture. */
		async setSemitones(n) {
			const next = clampSemitones(n);
			current = next;
			if (next === 0 && !ctx) return next; // never touched; nothing to undo
			ensureGraph();
			if (ctx.state === 'suspended') await ctx.resume();
			if (next === 0) {
				route(false);
				return next;
			}
			await ensureStretch();
			// A later tap may have moved the key while the library loaded.
			const want = current;
			stretch.schedule({ active: true, semitones: want, formantCompensation: true });
			route(want !== 0);
			return want;
		},
		/**
		 * Mix another element into the same path, through its own gain.
		 * Call from a tap (the context may need to start). An element can be
		 * attached only ONCE in its life (createMediaElementSource), so keep
		 * the handle and reuse it. Resolves with { setGain(0..1) }.
		 */
		async attach(el) {
			ensureGraph();
			if (ctx.state === 'suspended') await ctx.resume();
			const src = ctx.createMediaElementSource(el);
			const gain = ctx.createGain();
			src.connect(gain);
			gain.connect(routedThroughStretch ? stretch : ctx.destination);
			const x = { src, gain };
			extras.push(x);
			return {
				setGain(v) {
					const n = Math.max(0, Math.min(1, Number(v) || 0));
					gain.gain.value = n;
				},
			};
		},
		async close() {
			try {
				if (ctx) await ctx.close();
			} catch (e) {
				/* already closed */
			}
			ctx = null;
			source = null;
			stretch = null;
			stretchPromise = null;
			routedThroughStretch = false;
			extras.length = 0;
		},
	};
};
